/* ------------------------------------------------------------------------- */
/* Constants and utility functions                                           */
/* ------------------------------------------------------------------------- */

// Create a string representation of a complex object, this gives more detail
// than JSON.stringify()...
function debugObject(object) {
  let out = '';

  // eslint-disable-next-line guard-for-in
  for (const key in object) {
    out += `${key}: "${object[key]}"; `;
  }

  return out;
}

/* ------------------------------------------------------------------------- */
/* End of constants and utility functions                                    */
/* ------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------- */
/* Logging and error handling wrappers; make it possible to send logging and */
/* error messages to the application in the future (initially, just send to  */
/* the console).                                                             */
/* ------------------------------------------------------------------------- */

function log(message) {
  // eslint-disable-next-line no-console
  console.log(message);
}

function warn(message) {
  // eslint-disable-next-line no-console
  console.warn(message);
}

function error(message) {
  // eslint-disable-next-line no-console
  console.error(message);
}

/* ------------------------------------------------------------------------- */
/* End logging and error handling wrappers                                   */
/* ------------------------------------------------------------------------- */


/* ------------------------------------------------------------------------- */
/* indexedDB support. This provides a wrapper around the underlying native   */
/* indexedDB functions to support async-await and also to implement the blog */
/* database structure.                                                       */
/*                                                                           */
/* Database structure:                                                       */
/*  - database "blog"                                                        */
/*    - object store: "nameValue" (current version: 1)                       */
/*      objects in the store have a "name" (primary key) and "value"         */
/*      - object key "cacheCount": number that keeps increasing, to tag to   */
/*        the end of an API call, to force bypassing any caching that may    */
/*        happen between client and server.                                  */
/*      - object key "notifications": the last notifications data returned   */
/*        from the server.                                                   */
/* ------------------------------------------------------------------------- */

const DB_NAME = 'violin';
const DB_VERSION = 1;

// local copy of the database handle
let databaseCache = null;

// Data storage using indexedDB

// Get a database handle; this should be awaited before each
// database activity. It will return the opened database; if the
// database is not yet open, it will be opened.
// Note: it actually always (re-) opens the database, because it seems the
// "cached" handle can become stale and not work anymore...
function getDatabaseImpl() {
  return new Promise((resolve, reject) => {
    const openRequest = indexedDB.open(DB_NAME, DB_VERSION);

    openRequest.onsuccess = () => {
      databaseCache = openRequest.result;

      // register a generic error handler
      databaseCache.onerror = () => {
        // generic error handler for errors that weren't handled before
        error(`Database error: ${openRequest.error?.message}`);
      };

      resolve(databaseCache);
    };

    openRequest.onerror = () => {
      warn('rejecting openDb()');
      reject(`opening database failed: ${openRequest.error?.message}`);
    };

    // To create (or upgrade, in the future) the database structure
    openRequest.onupgradeneeded = (event) => {
      databaseCache = openRequest.result;
      log(`open onUpgradeNeeded: database: ${debugObject(databaseCache)}`);

      switch (event.oldVersion) {
        case 0: // database doesn't exist yet
          log('Need to create database... ');
          // What we are storing is name-value pairs. The names
          // are 'cacheCount' and 'notifications'
          databaseCache.createObjectStore('nameValue', {keyPath: 'name'});
          break;
        case 1: // should not get current version in upgrade needed
        default: // shouldn't get any other versions
          warn(`Unrecognized old version for database: ${event.oldVersion}`);
          break;
      }
    };
  });
}

async function readFromDatabaseImpl(database, elementName) {
  return new Promise((resolve) => {
    const transaction = database.transaction('nameValue', 'readonly');
    const nameValue = transaction.objectStore('nameValue');
    const request = nameValue.get(elementName);
    request.onsuccess = function() {
      if (!request?.result) {
        error(`reading from database onSuccess: no result: ${debugObject(request)}`);
        resolve({});
        return;
      }
      const result = request.result.value;
      resolve(JSON.parse(result));
    };
    request.onerror = function() {
      error('error reading from database...');
      resolve({});
    };
  });
}

async function writeToDatabaseImpl(database, elementName, elementValue) {
  return new Promise((resolve) => {
    const transaction = database.transaction('nameValue', 'readwrite');
    const nameValue = transaction.objectStore('nameValue');
    const valueToWrite = JSON.stringify(elementValue);
    const request = nameValue.put({name: elementName, value: valueToWrite});
    request.onsuccess = function() {
      // return the actual value back to the caller
      log('written to database: ' + elementName + ' with value ' + valueToWrite);
      resolve(elementValue);
    };
    request.onerror = function() {
      error('error writing to database...');
      resolve({});
    };
  });
}

/* ------------------------------------------------------------------------- */
/* End of indexedDB                                                          */
/* ------------------------------------------------------------------------- */

const databaseUtils = {
  getDatabase: async function() {
    return getDatabaseImpl();
  },

  readFromDatabase: async function(database, elementName) {
    return readFromDatabaseImpl(database, elementName);
  },

  writeToDatabase: async function(database, elementName, elementValue) {
    return writeToDatabaseImpl(database, elementName, elementValue);
  }
};

export default databaseUtils;
